import React from 'react';
import { LoadStatus } from '../../../../types/dashboard.types';
import Card, { cardStyles } from '../../../components/ui/Card/Card';
import { STATUS_DEFS, canonStatus, statusColorByName } from '../../Loads/statusMeta';
import { isInProgress } from './loadProgress';
import styles from './LoadsByStatusCard.module.css';

/**
 * CUÁNTAS CARGAS HAY EN CADA ESTATUS (20-oct-2026; rediseño de esa misma tarde).
 *
 * Va en la columna derecha del dashboard del cliente, en lugar de *"Find every metric here"*, que era relleno. Usa
 * `loadsByStatus`, que el API ya mandaba a todo el que tiene `LOADS` o `LD_VIEW` y nadie mostraba.
 *
 * - **Arriba, el total grande y cuántas siguen en camino**: es la pregunta que se hace primero.
 * - **Una barra partida por estatus**: la proporción de un vistazo. Cada tramo tiene un mínimo para que un estatus con
 *   1 carga junto a uno con 170 se siga viendo.
 * - **Abajo, cada estatus con su número y su porcentaje**, en el orden del ciclo de una carga (el de la tabla de
 *   cargas); uno que no esté ahí va al final. La barra es decorativa: los números van escritos en la lista.
 */

interface LoadsByStatusCardProps {
  data?: LoadStatus[];
}

const order = (name: string): number => {
  const i = STATUS_DEFS.findIndex((d) => canonStatus(d.key) === canonStatus(name));
  return i === -1 ? STATUS_DEFS.length : i;
};

const label = (name: string): string =>
  STATUS_DEFS.find((d) => canonStatus(d.key) === canonStatus(name))?.label || name;

/** "<1%" en lugar de "0%" para lo que sí existe: un 0 escrito dice que no hay ninguna. */
const percent = (count: number, total: number): string => {
  const p = total ? (count / total) * 100 : 0;
  return p > 0 && p < 1 ? '<1%' : `${Math.round(p)}%`;
};

const LoadsByStatusCard: React.FC<LoadsByStatusCardProps> = ({ data = [] }) => {
  const rows = data
    .filter((s) => s.count > 0)
    .sort((a, b) => order(a.status_name) - order(b.status_name) || b.count - a.count);
  const total = rows.reduce((sum, s) => sum + s.count, 0);
  const inProgress = rows.filter((s) => isInProgress(s.status_name)).reduce((sum, s) => sum + s.count, 0);

  return (
    <Card className={styles.card}>
      <div className={cardStyles.header}>
        <h3 className={cardStyles.title}>Loads by status</h3>
      </div>

      {rows.length === 0 ? (
        <p className={styles.empty}>No loads yet.</p>
      ) : (
        <>
          <div className={styles.summary}>
            <span className={styles.total}>{total.toLocaleString()}</span>
            <span className={styles.totalLabel}>
              {total === 1 ? 'load' : 'loads'}
              {inProgress > 0 && <span className={styles.chip}>{inProgress.toLocaleString()} in progress</span>}
            </span>
          </div>

          <div className={styles.stack} aria-hidden="true">
            {rows.map((s, i) => (
              <span
                key={s.status_id}
                className={styles.segment}
                title={`${label(s.status_name)}: ${s.count.toLocaleString()}`}
                style={
                  {
                    '--status': statusColorByName(s.status_name),
                    '--delay': `${i * 70}ms`,
                    flexGrow: s.count,
                  } as React.CSSProperties
                }
              />
            ))}
          </div>

          <ul className={styles.list}>
            {rows.map((s) => (
              <li
                key={s.status_id}
                className={styles.item}
                style={{ '--status': statusColorByName(s.status_name) } as React.CSSProperties}
              >
                <span className={styles.dot} aria-hidden="true" />
                <span className={styles.label}>{label(s.status_name)}</span>
                <span className={styles.count}>{s.count.toLocaleString()}</span>
                <span className={styles.pct}>{percent(s.count, total)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
};

export default LoadsByStatusCard;
